import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const UserManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roleCounts, setRoleCounts] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchUsers = useCallback(
    async (page = 1) => {
      const token = localStorage.getItem("ogestyle_token");
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({ page, limit: 50 });
        if (search) params.append("search", search);
        if (roleFilter) params.append("role", roleFilter);
        if (statusFilter) params.append("status", statusFilter);

        const response = await axios.get(
          `${API_BASE_URL}/api/admin/users?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setUsers(response.data.users);
        setPagination(response.data.pagination);
        if (response.data.roleCounts) {
          setRoleCounts(response.data.roleCounts);
        }
      } catch (error) {
        setError("Failed to load users");
      } finally {
        setLoading(false);
      }
    },
    [search, roleFilter, statusFilter],
  );

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchUsers();
    }
  }, [checkPortalAdmin, fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1);
  };

  const viewUserDetails = async (userId) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSelectedUser(response.data);
      setShowModal(true);
    } catch (error) {
      setError("Failed to load user details");
    }
  };

  const getRoleLabel = (roleCode) => {
    const labels = {
      R1: "Owner",
      R2: "Admin",
      R3: "Professional",
      R4: "Freelancer",
      R5: "Customer",
      R6: "Portal Admin",
      R7: "Booth Renter",
      R8: "Support Agent",
    };
    return labels[roleCode] || roleCode;
  };

  const getRoleBadgeColor = (roleCode) => {
    const colors = {
      R1: "#8b5cf6",
      R2: "#3b82f6",
      R3: "#10b981",
      R4: "#f59e0b",
      R5: "#6b7280",
      R6: "#ef4444",
      R7: "#4f46e5",
      R8: "#0ea5e9",
    };
    return colors[roleCode] || "#6b7280";
  };

  const getLastLoginValue = (user) =>
    user?.lastLoginAt || user?.lastLogin || null;

  const getLastLoginDisplay = (user) => formatDate(getLastLoginValue(user));

  const getLiveStatusDisplay = (user) =>
    user?.isOnline ? "Online now" : "Offline";

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && users.length === 0) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div
        style={{
          maxWidth: "none",
          width: "100%",
          margin: "0 auto",
          padding: "24px",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{ fontSize: "28px", fontWeight: "700", margin: "0 0 8px" }}
          >
            👥 User Management
          </h1>
          <p style={{ color: "#6b7280", margin: 0 }}>
            View and manage all platform users
          </p>
        </div>

        {/* Role Cards */}
        {roleCounts && Object.keys(roleCounts).length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "24px",
              overflowX: "auto",
              paddingBottom: "4px",
            }}
          >
            {/* All Users Card */}
            <div
              onClick={() => {
                setRoleFilter("");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "" ? "#3b82f6" : "white",
                color: roleFilter === "" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "" ? "#3b82f6" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === ""
                    ? "0 2px 8px rgba(59, 130, 246, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "") {
                  e.currentTarget.style.borderColor = "#3b82f6";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(59, 130, 246, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.all || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                All Users
              </div>
            </div>

            {/* Owner Card */}
            <div
              onClick={() => {
                setRoleFilter("R1");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R1" ? "#8b5cf6" : "white",
                color: roleFilter === "R1" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R1" ? "#8b5cf6" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R1"
                    ? "0 2px 8px rgba(139, 92, 246, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R1") {
                  e.currentTarget.style.borderColor = "#8b5cf6";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(139, 92, 246, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R1") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R1 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Owners
              </div>
            </div>

            {/* Admin Card */}
            <div
              onClick={() => {
                setRoleFilter("R2");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R2" ? "#3b82f6" : "white",
                color: roleFilter === "R2" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R2" ? "#3b82f6" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R2"
                    ? "0 2px 8px rgba(59, 130, 246, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R2") {
                  e.currentTarget.style.borderColor = "#3b82f6";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(59, 130, 246, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R2") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R2 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Admins
              </div>
            </div>

            {/* Professional Card */}
            <div
              onClick={() => {
                setRoleFilter("R3");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R3" ? "#10b981" : "white",
                color: roleFilter === "R3" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R3" ? "#10b981" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R3"
                    ? "0 2px 8px rgba(16, 185, 129, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R3") {
                  e.currentTarget.style.borderColor = "#10b981";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(16, 185, 129, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R3") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R3 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Professionals
              </div>
            </div>

            {/* Freelancer Card */}
            <div
              onClick={() => {
                setRoleFilter("R4");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R4" ? "#f59e0b" : "white",
                color: roleFilter === "R4" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R4" ? "#f59e0b" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R4"
                    ? "0 2px 8px rgba(245, 158, 11, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R4") {
                  e.currentTarget.style.borderColor = "#f59e0b";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(245, 158, 11, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R4") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R4 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Freelancers
              </div>
            </div>

            {/* Customer Card */}
            <div
              onClick={() => {
                setRoleFilter("R5");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R5" ? "#6b7280" : "white",
                color: roleFilter === "R5" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R5" ? "#6b7280" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R5"
                    ? "0 2px 8px rgba(107, 114, 128, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R5") {
                  e.currentTarget.style.borderColor = "#6b7280";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(107, 114, 128, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R5") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R5 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Customers
              </div>
            </div>

            {/* Portal Admin Card */}
            <div
              onClick={() => {
                setRoleFilter("R6");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R6" ? "#ef4444" : "white",
                color: roleFilter === "R6" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R6" ? "#ef4444" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R6"
                    ? "0 2px 8px rgba(239, 68, 68, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R6") {
                  e.currentTarget.style.borderColor = "#ef4444";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(239, 68, 68, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R6") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R6 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Portal Admins
              </div>
            </div>

            {/* Booth Renter Card */}
            <div
              onClick={() => {
                setRoleFilter("R7");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R7" ? "#4f46e5" : "white",
                color: roleFilter === "R7" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R7" ? "#4f46e5" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R7"
                    ? "0 2px 8px rgba(79, 70, 229, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R7") {
                  e.currentTarget.style.borderColor = "#4f46e5";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(79, 70, 229, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R7") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R7 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Booth Renters
              </div>
            </div>
            {/* Support Agent Card */}
            <div
              onClick={() => {
                setRoleFilter("R8");
                fetchUsers(1);
              }}
              style={{
                background: roleFilter === "R8" ? "#0ea5e9" : "white",
                color: roleFilter === "R8" ? "white" : "#1a1a1a",
                border: `2px solid ${roleFilter === "R8" ? "#0ea5e9" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "all 0.2s",
                minWidth: "120px",
                textAlign: "center",
                boxShadow:
                  roleFilter === "R8"
                    ? "0 2px 8px rgba(14, 165, 233, 0.15)"
                    : "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onMouseEnter={(e) => {
                if (roleFilter !== "R8") {
                  e.currentTarget.style.borderColor = "#0ea5e9";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(14, 165, 233, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (roleFilter !== "R8") {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow =
                    "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                {roleCounts.R8 || 0}
              </div>
              <div
                style={{ fontSize: "12px", opacity: 0.9, fontWeight: "500" }}
              >
                Support Agents
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        {/* Search and Filters */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <form
            onSubmit={handleSearch}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              alignItems: "flex-end",
            }}
          >
            <div style={{ flex: "1 1 300px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Search
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div style={{ width: "150px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All Roles</option>
                <option value="R1">Owner</option>
                <option value="R2">Admin</option>
                <option value="R3">Professional</option>
                <option value="R4">Freelancer</option>
                <option value="R5">Customer</option>
                <option value="R6">Portal Admin</option>
                <option value="R7">Booth Renter</option>
                <option value="R8">Support Agent</option>
              </select>
            </div>
            <div style={{ width: "150px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All Status</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
            <button
              type="submit"
              style={{
                padding: "10px 24px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              🔍 Search
            </button>
          </form>
        </div>

        {/* Results Summary */}
        <div
          style={{ marginBottom: "16px", color: "#6b7280", fontSize: "14px" }}
        >
          Showing {users.length} of {pagination.total} users
        </div>

        {/* Users Table */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    User
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Roles
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Created
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Last Login
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Live Status
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user._id}
                    style={{ borderBottom: "1px solid #f3f4f6" }}
                  >
                    <td style={{ padding: "14px 16px" }}>
                      <div>
                        <div style={{ fontWeight: "600", color: "#1a1a1a" }}>
                          {user.name}
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {(user.roles || []).map((role) => (
                          <span
                            key={role}
                            style={{
                              display: "inline-block",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                              background: `${getRoleBadgeColor(role)}15`,
                              color: getRoleBadgeColor(role),
                            }}
                          >
                            {getRoleLabel(role)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "9999px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background: user.verified ? "#d1fae5" : "#fee2e2",
                          color: user.verified ? "#065f46" : "#991b1b",
                        }}
                      >
                        {user.verified ? "✓ Verified" : "✗ Unverified"}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", color: "#6b7280" }}>
                      {formatDate(user.createdAt)}
                    </td>
                    <td style={{ padding: "14px 16px", color: "#6b7280" }}>
                      {getLastLoginDisplay(user)}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "9999px",
                          fontSize: "12px",
                          fontWeight: "600",
                          background: user.isOnline ? "#d1fae5" : "#f3f4f6",
                          color: user.isOnline ? "#065f46" : "#4b5563",
                        }}
                      >
                        {getLiveStatusDisplay(user)}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      <button
                        onClick={() => viewUserDetails(user._id)}
                        style={{
                          padding: "6px 12px",
                          background: "#eff6ff",
                          color: "#1e40af",
                          border: "1px solid #bfdbfe",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div
              style={{
                padding: "16px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => fetchUsers(page)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border:
                        pagination.page === page
                          ? "2px solid #3b82f6"
                          : "1px solid #e5e7eb",
                      background:
                        pagination.page === page ? "#eff6ff" : "white",
                      color: pagination.page === page ? "#1e40af" : "#6b7280",
                      fontWeight: pagination.page === page ? "600" : "400",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {page}
                  </button>
                ),
              )}
            </div>
          )}
        </div>

        {/* User Details Modal */}
        {showModal && selectedUser && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "700px",
                maxHeight: "90vh",
                overflow: "auto",
                padding: "32px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "24px",
                }}
              >
                <div>
                  <h2
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      margin: "0 0 4px",
                    }}
                  >
                    {selectedUser.user?.name}
                  </h2>
                  <p style={{ color: "#6b7280", margin: 0 }}>
                    {selectedUser.user?.email}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#6b7280",
                    padding: "8px",
                  }}
                >
                  ×
                </button>
              </div>

              {/* User Info */}
              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                  }}
                >
                  Account Info
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      User ID
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        fontFamily: "monospace",
                      }}
                    >
                      {selectedUser.user?._id}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Phone
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>
                      {selectedUser.user?.phone || "Not set"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Created
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>
                      {formatDate(selectedUser.user?.createdAt)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Last Login
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>
                      {selectedUser.user?.isOnline
                        ? "Online now"
                        : formatDate(
                            selectedUser.user?.lastLoginAt ||
                              selectedUser.user?.lastLogin,
                          )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Staff Records */}
              {selectedUser.staffRecords?.length > 0 && (
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginBottom: "16px",
                    }}
                  >
                    Staff Records
                  </h3>
                  {selectedUser.staffRecords.map((staff, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "12px",
                        background: "white",
                        borderRadius: "8px",
                        marginBottom: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div style={{ fontWeight: "600" }}>
                        {staff.businessId?.name || "Unknown Business"}
                      </div>
                      <div style={{ fontSize: "13px", color: "#6b7280" }}>
                        Role: {getRoleLabel(staff.role)} | Freelancer:{" "}
                        {staff.isFreelancer ? "Yes" : "No"}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => setShowModal(false)}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#f3f4f6",
                  color: "#6b7280",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default UserManagement;
